import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Wrench, ArrowRight, CheckCircle2, XCircle, FileDown, Paperclip } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import PhotoUploader from "@/components/PhotoUploader";
import { formatDate } from "@/lib/format";

/**
 * WorkRequestsPanel — replaces the legacy Work Orders widget on the Command page.
 * Lists all OPEN internally-generated work requests linked to actions.
 * Admin can close each with either a completed Work Order (+ optional PDF attachment)
 * or an explanation for denial.
 */
export default function WorkRequestsPanel() {
  const [rows, setRows] = useState([]);
  const [showClosed, setShowClosed] = useState(false);
  const [closeTarget, setCloseTarget] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const load = () => {
    api.get("/work-requests").then((r) => {
      setRows(r.data || []);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  };
  useEffect(() => { load(); }, []);

  const openRows = rows.filter((r) => !(r.status || "").startsWith("closed"));
  const closedRows = rows.filter((r) => (r.status || "").startsWith("closed"));
  const view = showClosed ? closedRows : openRows;

  return (
    <div className="surface-card rounded-2xl p-6" data-testid="dash-work-requests-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Open work requests</div>
          <div className="font-display font-bold text-base">Internally-generated work requests</div>
        </div>
        <div className="flex bg-[#f7f5f2] rounded-full p-1">
          <button data-testid="wr-tab-open" onClick={() => setShowClosed(false)}
            className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold ${!showClosed ? "bg-[#1a1a1a] text-white" : "text-[#1a1a1a]/60"}`}>
            Open · {openRows.length}
          </button>
          <button data-testid="wr-tab-closed" onClick={() => setShowClosed(true)}
            className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold ${showClosed ? "bg-[#1a1a1a] text-white" : "text-[#1a1a1a]/60"}`}>
            Closed · {closedRows.length}
          </button>
        </div>
      </div>

      {view.length === 0 ? (
        <div className="py-10 text-center bg-[#f7f5f2] rounded-xl border border-dashed border-[#1a1a1a]/15">
          <div className="text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">
            {loaded ? (showClosed ? "No closed requests yet." : "No open work requests.") : "Loading…"}
          </div>
        </div>
      ) : (
        <ul className="divide-y divide-[#1a1a1a]/08" data-testid="wr-list">
          {view.slice(0, 8).map((w) => (
            <li key={w.id} className="py-3 flex items-start gap-3" data-testid={`wr-row-${w.id}`}>
              <div className="w-9 h-9 rounded-xl bg-[#fff1ee] flex items-center justify-center shrink-0">
                <Wrench className="w-4 h-4 text-[#ff4500]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Link to={`/app/actions/${w.action_id}`}
                    className="font-display font-bold text-sm truncate hover:text-[#ff4500]">
                    {w.action_display_id || "—"} · {w.action_title || w.subject}
                  </Link>
                </div>
                <div className="text-[10px] font-mono text-[#1a1a1a]/55 uppercase tracking-wider mt-0.5">
                  Sent by {w.sent_by_name} · to {w.to_email} · {formatDate(w.created_at)}
                </div>
                {(w.status || "").startsWith("closed") && (
                  <div className="text-[11px] mt-1">
                    {w.status === "closed_completed" ? (
                      <span className="text-[#1a8754] font-mono">
                        <CheckCircle2 className="w-3 h-3 inline mr-1" />
                        WO # {w.work_order_number} · closed by {w.closed_by_name}
                      </span>
                    ) : (
                      <span className="text-[#1a1a1a]/70 font-mono">
                        <XCircle className="w-3 h-3 inline mr-1" />
                        Denied by {w.closed_by_name}: {w.explanation}
                      </span>
                    )}
                    {w.completed_pdf_file_id && (
                      <a href={`${window.location.origin.replace("preview.emergentagent.com", "emergentagent.com")}/api/files/${w.completed_pdf_file_id}?auth=${encodeURIComponent(localStorage.getItem("opscmd_token") || "")}`}
                        target="_blank" rel="noreferrer"
                        className="ml-2 text-[#1a5fb4] hover:underline inline-flex items-center gap-1">
                        <FileDown className="w-3 h-3" /> WO PDF
                      </a>
                    )}
                  </div>
                )}
              </div>
              {!(w.status || "").startsWith("closed") && (
                <button data-testid={`wr-close-btn-${w.id}`} onClick={() => setCloseTarget(w)}
                  className="btn-pill btn-pill-secondary text-[11px]">
                  Close <ArrowRight className="w-3 h-3" />
                </button>
              )}
            </li>
          ))}
          {view.length > 8 && (
            <li className="pt-3 text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/50">
              +{view.length - 8} more — open Actions to review
            </li>
          )}
        </ul>
      )}

      <CloseWorkRequestDialog wr={closeTarget} onClose={() => setCloseTarget(null)} onDone={load} />
    </div>
  );
}

function CloseWorkRequestDialog({ wr, onClose, onDone }) {
  const [resolution, setResolution] = useState("completed");
  const [woNumber, setWoNumber] = useState("");
  const [pdfIds, setPdfIds] = useState([]);
  const [explanation, setExplanation] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (wr) {
      setResolution("completed");
      setWoNumber("");
      setPdfIds([]);
      setExplanation("");
    }
  }, [wr?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (resolution === "completed" && !woNumber.trim()) {
      return toast.error("Work Order # is required.");
    }
    if (resolution === "denied" && !explanation.trim()) {
      return toast.error("Denial explanation is required.");
    }
    setBusy(true);
    try {
      await api.post(`/work-requests/${wr.id}/close`, {
        resolution,
        work_order_number: resolution === "completed" ? woNumber.trim() : null,
        completed_pdf_file_id: resolution === "completed" && pdfIds[0] ? pdfIds[0] : null,
        explanation: resolution === "denied" ? explanation.trim() : (explanation.trim() || null),
      });
      toast.success("Work request closed.");
      onDone?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to close.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!wr} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-lg flex items-center gap-2">
            <Wrench className="w-4 h-4 text-[#ff4500]" /> Close work request
          </DialogTitle>
        </DialogHeader>
        {wr && (
          <div className="space-y-4">
            <div className="bg-[#f7f5f2] rounded-xl p-3 text-[12px]">
              <div className="font-display font-bold text-sm">{wr.action_display_id} · {wr.action_title}</div>
              <div className="text-[11px] font-mono text-[#1a1a1a]/60 uppercase tracking-wider mt-0.5">
                Sent to {wr.to_email}
              </div>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.18em] font-mono text-[#1a1a1a]/65 mb-1.5">
                Resolution
              </label>
              <div className="flex gap-2">
                {[
                  ["completed", "Completed", CheckCircle2, "#1a8754"],
                  ["denied", "Denied / no work needed", XCircle, "#1a1a1a"],
                ].map(([k, l, Icon, c]) => (
                  <button key={k} data-testid={`wr-resolution-${k}`} onClick={() => setResolution(k)} type="button"
                    className={`flex-1 border-2 rounded-xl p-3 text-left transition-colors ${
                      resolution === k ? "border-[#ff4500] bg-[#fff1ea]" : "border-[#1a1a1a]/10 hover:border-[#1a1a1a]/30"
                    }`}>
                    <Icon className="w-4 h-4 mb-1" style={{ color: c }} />
                    <div className="font-display font-bold text-sm">{l}</div>
                  </button>
                ))}
              </div>
            </div>
            {resolution === "completed" ? (
              <>
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.18em] font-mono text-[#1a1a1a]/65 mb-1.5">
                    Work Order # *
                  </label>
                  <input data-testid="wr-wo-input" value={woNumber} onChange={(e) => setWoNumber(e.target.value)}
                    placeholder="e.g. WO-2026-00429"
                    className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.18em] font-mono text-[#1a1a1a]/65 mb-1.5">
                    <Paperclip className="w-3 h-3 inline mr-1" /> Attach completed Work Order PDF
                  </label>
                  <PhotoUploader photoIds={pdfIds} onChange={setPdfIds} testIdPrefix="wr-pdf" accept="application/pdf" />
                  <div className="text-[10px] text-[#1a1a1a]/55 mt-1">
                    Optional but recommended. The PDF is auto-linked to the source Action for the audit trail.
                  </div>
                </div>
              </>
            ) : (
              <div>
                <label className="block text-[11px] uppercase tracking-[0.18em] font-mono text-[#1a1a1a]/65 mb-1.5">
                  Explanation *
                </label>
                <textarea data-testid="wr-denial-explanation" rows={4}
                  value={explanation} onChange={(e) => setExplanation(e.target.value)}
                  placeholder='e.g. "Maintenance checked the device — fully operational, no work needed."'
                  className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm" />
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <button onClick={onClose} className="btn-pill btn-pill-ghost text-[11px]">Cancel</button>
          <button data-testid="wr-close-submit" onClick={submit} disabled={busy}
            className="btn-pill btn-pill-primary text-[11px]">
            {busy ? "Closing…" : "Close request"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
